import React, { useRef, useCallback } from 'react';

/**
 * Succeedo design system slider.
 * Orange filled range (left), light grey track (right), white thumb with border.
 * No external dependencies — pure React + CSS.
 *
 * Props:
 *   min, max, step, value, onChange, showValue, formatValue
 */
export default function Slider({
  min = 0,
  max = 100,
  step = 1,
  value = 50,
  onChange,
  showValue = false,
  formatValue,
}) {
  const trackRef = useRef(null);
  const pct = ((value - min) / (max - min)) * 100;

  const handleInteraction = useCallback((clientX) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const raw = min + ratio * (max - min);
    const stepped = Math.round(raw / step) * step;
    const clamped = Math.max(min, Math.min(max, stepped));
    if (onChange) onChange(clamped);
  }, [min, max, step, onChange]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    handleInteraction(e.clientX);

    const onMove = (ev) => handleInteraction(ev.clientX);
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [handleInteraction]);

  const handleTouchStart = useCallback((e) => {
    handleInteraction(e.touches[0].clientX);

    const onMove = (ev) => handleInteraction(ev.touches[0].clientX);
    const onEnd = () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
    document.addEventListener('touchmove', onMove);
    document.addEventListener('touchend', onEnd);
  }, [handleInteraction]);

  return (
    <div className="relative w-full select-none touch-none">
      {showValue && (
        <div className="text-[15px] font-semibold text-foreground mb-2">
          {formatValue ? formatValue(value) : value}
        </div>
      )}
      <div
        ref={trackRef}
        className="relative h-1.5 w-full rounded-full cursor-pointer"
        style={{ backgroundColor: 'hsl(var(--border))' }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        role="slider"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (onChange) onChange(Math.min(max, value + step));
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
            e.preventDefault();
            if (onChange) onChange(Math.max(min, value - step));
          }
        }}
      >
        {/* Filled range */}
        <div
          className="absolute top-0 left-0 h-full rounded-full"
          style={{
            width: `${pct}%`,
            backgroundColor: 'hsl(var(--succeedo-orange))',
          }}
        />
        {/* Thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2"
          style={{
            left: `${pct}%`,
            transform: `translate(-50%, -50%)`,
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: 'white',
            border: '2px solid hsl(var(--succeedo-orange))',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          }}
        />
      </div>
    </div>
  );
}
